import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import config from '../../config';

const LoginForm = ({ onSuccess, onSwitchToRegister, onForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerificationRequired(false);
    setLoading(true);

    try {
      const response = await fetch(`${config.API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.verificationRequired) {
          setVerificationRequired(true);
          setError('Veuillez vérifier votre email avant de vous connecter.');
        } else {
          setError(data.error || 'Erreur de connexion');
        }
        return;
      }

      // Stocker le token et les infos utilisateur
      localStorage.setItem('kokpeet_token', data.token);

      if (onSuccess) {
        onSuccess(data.user, data.token);
      }
    } catch (err) {
      console.error('Erreur connexion:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setVerificationSent(false);

    try {
      const response = await fetch(`${config.API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setVerificationSent(true);
      }
    } catch (err) {
      console.error('Erreur renvoi vérification:', err);
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {error && (
        <div className="auth-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {verificationRequired && (
        <div className="auth-verification-notice">
          <p>Un email de vérification vous a été envoyé lors de votre inscription.</p>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={resendingVerification}
            className="resend-btn"
          >
            {resendingVerification ? (
              <>
                <Loader2 size={14} className="spin" />
                Envoi en cours...
              </>
            ) : verificationSent ? (
              'Email envoyé !'
            ) : (
              'Renvoyer l\'email de vérification'
            )}
          </button>
        </div>
      )}

      <div className="form-group">
        <div className="input-with-icon">
          <Mail size={18} className="input-icon" />
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            required
            autoComplete="email"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="input-with-icon">
          <Lock size={18} className="input-icon" />
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Votre mot de passe"
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="forgot-password-link"
          onClick={onForgotPassword}
        >
          Mot de passe oublié ?
        </button>
      </div>

      <button type="submit" className="auth-submit-btn" disabled={loading}>
        {loading ? (
          <>
            <Loader2 size={18} className="spin" />
            Connexion...
          </>
        ) : (
          'Se connecter'
        )}
      </button>

      <div className="auth-switch">
        <span>Nouveau sur Kokpit ?</span>
        <button type="button" onClick={onSwitchToRegister}>
          Créez un compte
        </button>
      </div>

      <p className="auth-legal">
        En continuant, vous acceptez nos <a href="/terms" target="_blank" rel="noopener noreferrer">Conditions d'utilisation</a> et notre <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a>.
      </p>
    </form>
  );
};

export default LoginForm;
